import {
  COLOR_TOKENS,
  FONT_TOKENS,
  RADIUS_TOKENS,
  SPACING_TOKENS,
} from "~/designSystem/constants/token";

import {
  ButtonIconContainerColor,
  ButtonIconType,
  ButtonVariant,
} from "./enums";

export const BUTTON_SIZES_MAP = {
  "x-small": {
    height: "26px",
    padding: SPACING_TOKENS.spacing["layout-spacing-6"],
    gap: SPACING_TOKENS.spacing["layout-spacing-6"],
    radius: RADIUS_TOKENS.radius["radius-5"],
  },
  small: {
    height: "28px",
    padding: SPACING_TOKENS.spacing["layout-spacing-6"],
    gap: SPACING_TOKENS.spacing["layout-spacing-6"],
    radius: RADIUS_TOKENS.radius["radius-6"],
  },
  medium: {
    height: "32px",
    padding: `${SPACING_TOKENS.spacing["layout-spacing-6"]} ${SPACING_TOKENS.spacing["layout-spacing-8"]}`,
    gap: SPACING_TOKENS.spacing["layout-spacing-7"],
    radius: RADIUS_TOKENS.radius["radius-6"],
  },
  large: {
    height: "36px",
    padding: `${SPACING_TOKENS.spacing["layout-spacing-6"]} ${SPACING_TOKENS.spacing["layout-spacing-8"]}`,
    gap: SPACING_TOKENS.spacing["layout-spacing-7"],
    radius: RADIUS_TOKENS.radius["radius-6"],
  },
  "x-large": {
    height: "48px",
    padding: SPACING_TOKENS.spacing["layout-spacing-10"],
    gap: SPACING_TOKENS.spacing["layout-spacing-10"],
    radius: RADIUS_TOKENS.radius["radius-8"],
  },
};

export const BUTTON_TYPOGRAPHY = {
  size: FONT_TOKENS.size["size-2"],
  weight: FONT_TOKENS.weight.medium,
  lineHeight: FONT_TOKENS.height["height-2"],
  spacing: FONT_TOKENS.spacing["spacing-2"],
};

export const BUTTON_VARIANTS = {
  primary: {
    background: {
      default: COLOR_TOKENS.core.silver[12],
      hover: `linear-gradient(0deg, ${COLOR_TOKENS.base[1]} 0%, ${COLOR_TOKENS.base[1]} 100%), ${COLOR_TOKENS.core.silver[12]}`,
      focus: COLOR_TOKENS.core.silver[12],
      loading: COLOR_TOKENS.core.silver[12],
      disabled: COLOR_TOKENS.core.silver.a6,
    },
    border: COLOR_TOKENS.core.silver.a4,
    text: {
      default: COLOR_TOKENS.core.silver[1],
      hover: COLOR_TOKENS.core.silver[1],
      focus: COLOR_TOKENS.core.silver[1],
      disabled: COLOR_TOKENS.core.silver[2],
    },
  },
  accent: {
    background: {
      default: COLOR_TOKENS.core.blue[9],
      hover: COLOR_TOKENS.core.blue.a10,
      focus: COLOR_TOKENS.core.blue.a10,
      loading: COLOR_TOKENS.core.blue[9],
      disabled: COLOR_TOKENS.core.blue.a6,
    },
    border: COLOR_TOKENS.core.silver.a4,
    text: {
      default: COLOR_TOKENS.white.a12,
      hover: COLOR_TOKENS.white.a12,
      focus: COLOR_TOKENS.white.a12,
      disabled: COLOR_TOKENS.white.a10,
    },
  },
  secondary: {
    background: {
      default: COLOR_TOKENS.base[12],
      hover: `linear-gradient(0deg, ${COLOR_TOKENS.core.silver.a2} 0%, ${COLOR_TOKENS.core.silver.a2} 100%), ${COLOR_TOKENS.base[12]}`,
      focus: COLOR_TOKENS.base[12],
      loading: COLOR_TOKENS.base[12],
      disabled: COLOR_TOKENS.base[12],
    },
    border: {
      default: COLOR_TOKENS.core.silver.a5,
      focus: COLOR_TOKENS.core.silver.a9,
      disabled: COLOR_TOKENS.core.silver.a4,
    },
    text: {
      default: COLOR_TOKENS.core.silver.a12,
      hover: COLOR_TOKENS.core.silver.a12,
      focus: COLOR_TOKENS.core.silver.a12,
      disabled: COLOR_TOKENS.core.silver.a7,
    },
  },
  shadow: {
    background: {
      default: COLOR_TOKENS.core.silver[1],
      hover: `linear-gradient(0deg, ${COLOR_TOKENS.core.silver.a2} 0%, ${COLOR_TOKENS.core.silver.a2} 100%), ${COLOR_TOKENS.core.silver[1]}`,
      focus: COLOR_TOKENS.core.silver[1],
      disabled: COLOR_TOKENS.core.silver[1],
      loading: COLOR_TOKENS.core.silver[1],
    },
    boxShadow: {
      default: `0px 0px 0px 1px ${COLOR_TOKENS.gray.a4}, 0px 2px 3px -2px ${COLOR_TOKENS.gray.a3}, 0px 3px 12px -4px ${COLOR_TOKENS.black.a2}, 0px 4px 16px -8px ${COLOR_TOKENS.black.a2}`,
      focus: `0px 0px 0px 3px ${COLOR_TOKENS.core.silver.a6}, 0px 0px 0px 1px ${COLOR_TOKENS.gray.a9}, 0px 2px 3px -2px ${COLOR_TOKENS.gray.a3}, 0px 3px 12px -4px ${COLOR_TOKENS.black.a2}, 0px 4px 16px -8px ${COLOR_TOKENS.black.a2}`,
    },
    text: {
      default: COLOR_TOKENS.core.silver.a12,
      hover: COLOR_TOKENS.core.silver.a12,
      focus: COLOR_TOKENS.core.silver.a12,
      disabled: COLOR_TOKENS.core.silver.a7,
    },
  },
  ghost: {
    background: {
      hover: COLOR_TOKENS.core.silver.a4,
      focus: `transparent`,
    },
    text: {
      default: COLOR_TOKENS.core.silver.a12,
      hover: COLOR_TOKENS.core.silver.a12,
      focus: COLOR_TOKENS.core.silver.a12,
      disabled: COLOR_TOKENS.core.silver.a7,
    },
    border: {
      default: "transparent",
      focus: COLOR_TOKENS.core.silver.a9,
    },
    boxShadow: {
      focus: `0px 0px 0px 3px ${COLOR_TOKENS.core.silver.a6}`,
    },
  },
  danger: {
    background: {
      default: COLOR_TOKENS.status.error[9],
      hover: COLOR_TOKENS.status.error[10],
      focus: COLOR_TOKENS.status.error[10],
      loading: COLOR_TOKENS.status.error[9],
      disabled: COLOR_TOKENS.status.error[6],
    },
    boxShadow: {
      focus: `0px 0px 0px 3px ${COLOR_TOKENS.status.error.a5}`,
    },
    border: {
      default: COLOR_TOKENS.core.silver.a4,
    },
    text: {
      default: COLOR_TOKENS.white.a12,
      disabled: COLOR_TOKENS.white.a10,
    },
  },
  warning: {
    background: {
      default: COLOR_TOKENS.status.warning[9],
      hover: COLOR_TOKENS.status.warning[10],
      focus: COLOR_TOKENS.status.warning[10],
      loading: COLOR_TOKENS.status.warning[9],
      disabled: COLOR_TOKENS.status.warning[6],
    },
    boxShadow: {
      focus: `0px 0px 0px 3px ${COLOR_TOKENS.status.warning.a5}`,
    },
    border: {
      default: COLOR_TOKENS.core.silver.a4,
    },
    text: {
      default: COLOR_TOKENS.white.a12,
      disabled: COLOR_TOKENS.white.a10,
    },
  },
  success: {
    background: {
      default: COLOR_TOKENS.status.success[9],
      hover: COLOR_TOKENS.status.success[10],
      focus: COLOR_TOKENS.status.success[10],
      loading: COLOR_TOKENS.status.success[9],
      disabled: COLOR_TOKENS.status.success[6],
    },
    boxShadow: {
      focus: `0px 0px 0px 3px ${COLOR_TOKENS.status.success.a5}`,
    },
    border: {
      default: COLOR_TOKENS.core.silver.a4,
    },
    text: {
      default: COLOR_TOKENS.white.a12,
      disabled: COLOR_TOKENS.white.a10,
    },
  },
} as const;

export const BUTTON_STYLED_PROPS = {
  base: ["$size", "$disabled", "$loading"],
  primary: ["$size", "$loading"],
  accent: ["$size", "$loading"],
  secondary: ["$size", "$loading"],
  shadow: ["$size", "$loading"],
  ghost: ["$size", "$loading"],
  danger: ["$size", "$loading"],
  warning: ["$size", "$loading"],
  success: ["$size", "$loading"],
  icon: ["$type"],
} as const;

export const ICON_COLOR_MAP = {
  [ButtonVariant.PRIMARY]: {
    default: COLOR_TOKENS.core.silver[1],
    disabled: COLOR_TOKENS.white.a12,
  },
  [ButtonVariant.ACCENT]: {
    default: COLOR_TOKENS.white.a12,
    disabled: COLOR_TOKENS.white.a12,
  },
  [ButtonVariant.SECONDARY]: {
    default: COLOR_TOKENS.core.silver[12],
    disabled: COLOR_TOKENS.gray[7],
  },
  [ButtonVariant.SHADOW]: {
    default: COLOR_TOKENS.core.silver[12],
    disabled: COLOR_TOKENS.gray[7],
  },
  [ButtonVariant.GHOST]: {
    default: COLOR_TOKENS.core.silver[12],
    disabled: COLOR_TOKENS.gray[7],
  },
  [ButtonVariant.DANGER]: {
    default: COLOR_TOKENS.white.a12,
    disabled: COLOR_TOKENS.white.a12,
  },
  [ButtonVariant.WARNING]: {
    default: COLOR_TOKENS.white.a12,
    disabled: COLOR_TOKENS.white.a12,
  },
  [ButtonVariant.SUCCESS]: {
    default: COLOR_TOKENS.white.a12,
    disabled: COLOR_TOKENS.white.a12,
  },
} as const;

export const BUTTON_ICON_COLOR_MAP = {
  [ButtonIconType.DEFAULT]: {
    default: COLOR_TOKENS.core.silver.a9,
    hover: COLOR_TOKENS.core.silver.a12,
    focus: COLOR_TOKENS.core.blue.a9,
    active: COLOR_TOKENS.core.blue.a9,
    focusBoxShadowColor: COLOR_TOKENS.core.blue.a6,
  },
  [ButtonIconType.CORE_BLUE]: {
    default: COLOR_TOKENS.core.blue.a7,
    hover: COLOR_TOKENS.core.blue.a9,
    focus: COLOR_TOKENS.core.blue.a11,
    active: COLOR_TOKENS.core.blue.a11,
    focusBoxShadowColor: COLOR_TOKENS.core.blue.a6,
  },
  [ButtonIconType.DANGER]: {
    default: COLOR_TOKENS.status.error.a8,
    hover: COLOR_TOKENS.status.error.a9,
    focus: COLOR_TOKENS.status.error.a9,
    active: COLOR_TOKENS.status.error.a11,
    focusBoxShadowColor: COLOR_TOKENS.status.error.a5,
  },
  [ButtonIconType.SUCCESS]: {
    default: COLOR_TOKENS.status.success.a8,
    hover: COLOR_TOKENS.status.success.a9,
    focus: COLOR_TOKENS.status.success.a9,
    active: COLOR_TOKENS.status.success.a11,
    focusBoxShadowColor: COLOR_TOKENS.status.success.a5,
  },
  [ButtonIconType.WARNING]: {
    default: COLOR_TOKENS.status.warning.a8,
    hover: COLOR_TOKENS.status.warning.a9,
    focus: COLOR_TOKENS.status.warning.a9,
    active: COLOR_TOKENS.status.warning.a11,
    focusBoxShadowColor: COLOR_TOKENS.status.warning.a5,
  },
  [ButtonIconType.FEATURE]: {
    default: COLOR_TOKENS.status.feature.a8,
    hover: COLOR_TOKENS.status.feature.a10,
    focus: COLOR_TOKENS.status.feature.a9,
    active: COLOR_TOKENS.status.feature.a11,
    focusBoxShadowColor: COLOR_TOKENS.status.feature.a5,
  },
  [ButtonIconType.INFO]: {
    default: COLOR_TOKENS.status.info.a8,
    hover: COLOR_TOKENS.status.info.a11,
    focus: COLOR_TOKENS.status.info.a11,
    active: COLOR_TOKENS.status.info.a12,
    focusBoxShadowColor: COLOR_TOKENS.status.info.a5,
  },
} as const;

export const BUTTON_ICON_CONTAINER_COLOR_MAP = {
  [ButtonIconContainerColor.CORE_SILVER]: {
    hoverColor: COLOR_TOKENS.core.silver[11],
    hoverBG: COLOR_TOKENS.core.silver.a3,
    focusColor: COLOR_TOKENS.core.silver.a11,
    focusBG: COLOR_TOKENS.core.silver.a1,
    focusShadowColor: COLOR_TOKENS.core.silver.a4,
    focusBorderColor: COLOR_TOKENS.core.silver.a11,
  },
  [ButtonIconContainerColor.CORE_BLUE]: {
    hoverColor: COLOR_TOKENS.core.blue.a11,
    hoverBG: COLOR_TOKENS.core.blue.a3,
    focusColor: COLOR_TOKENS.core.blue.a11,
    focusBG: COLOR_TOKENS.core.blue.a1,
    focusShadowColor: COLOR_TOKENS.core.blue.a4,
    focusBorderColor: COLOR_TOKENS.core.blue.a11,
  },
  [ButtonIconContainerColor.DANGER]: {
    hoverColor: COLOR_TOKENS.status.error.a11,
    hoverBG: COLOR_TOKENS.status.error.a3,
    focusColor: COLOR_TOKENS.status.error.a11,
    focusBG: COLOR_TOKENS.status.error.a1,
    focusShadowColor: COLOR_TOKENS.status.error.a4,
    focusBorderColor: COLOR_TOKENS.status.error.a11,
  },
  [ButtonIconContainerColor.SUCCESS]: {
    hoverColor: COLOR_TOKENS.status.success.a11,
    hoverBG: COLOR_TOKENS.status.success.a3,
    focusColor: COLOR_TOKENS.status.success.a11,
    focusBG: COLOR_TOKENS.status.success.a1,
    focusShadowColor: COLOR_TOKENS.status.success.a4,
    focusBorderColor: COLOR_TOKENS.status.success.a11,
  },
  [ButtonIconContainerColor.WARNING]: {
    hoverColor: COLOR_TOKENS.status.warning.a11,
    hoverBG: COLOR_TOKENS.status.warning.a3,
    focusColor: COLOR_TOKENS.status.warning.a11,
    focusBG: COLOR_TOKENS.status.warning.a1,
    focusShadowColor: COLOR_TOKENS.status.warning.a4,
    focusBorderColor: COLOR_TOKENS.status.warning.a11,
  },
  [ButtonIconContainerColor.FEATURE]: {
    hoverColor: COLOR_TOKENS.status.feature.a11,
    hoverBG: COLOR_TOKENS.status.feature.a3,
    focusColor: COLOR_TOKENS.status.feature.a11,
    focusBG: COLOR_TOKENS.status.feature.a1,
    focusShadowColor: COLOR_TOKENS.status.feature.a4,
    focusBorderColor: COLOR_TOKENS.status.feature.a11,
  },
  [ButtonIconContainerColor.INFO]: {
    hoverColor: COLOR_TOKENS.status.info.a11,
    hoverBG: COLOR_TOKENS.status.info.a3,
    focusColor: COLOR_TOKENS.status.info.a11,
    focusBG: COLOR_TOKENS.status.info.a1,
    focusShadowColor: COLOR_TOKENS.status.info.a4,
    focusBorderColor: COLOR_TOKENS.status.info.a11,
  },
} as const;
